import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, Calendar, Check, ShieldAlert, RefreshCw } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AuthShell, AuthLogo, PrimaryButton, OutlineButton, GoogleIcon, AUTH_SUBTLE, AUTH_LABEL, AUTH_BLUE } from './authShared';

const ICON = '#98A2B3';

const TIERS = [
  { id: 'basic', name: 'Basic', price_monthly: 29, price_annual: 290, features: ['Up to 5 projects', 'Basic reporting', 'Email support'] },
  { id: 'pro', name: 'Pro', price_monthly: 69, price_annual: 690, features: ['Up to 25 projects', 'Advanced reporting', 'Priority support', 'Team collaboration'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price_monthly: 199, price_annual: 1990, features: ['Unlimited projects', 'Custom integrations', 'Dedicated support', 'API access'], contact_required: true },
];

const cardStyle = {
  style: {
    base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' }, fontFamily: 'inherit' },
    invalid: { color: '#ef4444' },
  },
};

function CardForm({ formData, discountCode, affiliateCode, onSuccess, setIsLoading, isLoading }) {
  const stripe = useStripe();
  const elements = useElements();
  const { executeRecaptcha, isConfigured } = useRecaptcha();
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsLoading(true);
    setCardError(null);
    try {
      let recaptchaToken = null;
      if (isConfigured) {
        try { recaptchaToken = await executeRecaptcha('signup'); } catch (err) { console.warn('reCAPTCHA failed:', err); }
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: { name: `${formData.firstName} ${formData.lastName}`, email: formData.email },
      });
      if (error) {
        setCardError(error.message);
        setIsLoading(false);
        return;
      }
      const signupPayload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.dateOfBirth,
        email: formData.email,
        password: formData.password,
        tier_id: formData.tier,
        billing_cycle: formData.billingCycle,
        payment_method_id: paymentMethod.id,
        recaptcha_token: recaptchaToken || '',
      };
      if (discountCode) signupPayload.discount_code = discountCode;
      if (affiliateCode) signupPayload.affiliate_code = affiliateCode;
      const response = await apiService.signupWithPayment(signupPayload);
      onSuccess(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create account';
      toast.error(message);
      setCardError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label style={{ color: AUTH_LABEL }}>Card Details</Label>
        <div className="bg-white rounded-md p-4 border" style={{ borderColor: '#E4E9F0' }}>
          <CardElement options={cardStyle} />
        </div>
        {cardError && (
          <p className="text-red-600 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {cardError}</p>
        )}
      </div>
      <div className="rounded-md p-4 border" style={{ borderColor: '#D6E4FF', background: '#EEF2FB' }}>
        <p className="text-sm" style={{ color: '#1D2939' }}>
          <strong>30-day free trial</strong> — Your card will not be charged until the trial ends. Cancel anytime.
        </p>
      </div>
      <PrimaryButton type="submit" disabled={!stripe || isLoading}>{isLoading ? 'Creating Account...' : 'Start Free Trial'}</PrimaryButton>
    </form>
  );
}

function Signup({ onLogin }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeStatus, setStripeStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const urlParams = new URLSearchParams(window.location.search);
  const planFromUrl = urlParams.get('plan');
  const refFromUrl = urlParams.get('ref');
  const emailFromUrl = urlParams.get('email') || '';
  const nameFromUrl = (urlParams.get('name') || '').trim();
  const [prefillFirst, ...prefillRest] = nameFromUrl ? nameFromUrl.split(' ') : [''];
  const discountFromUrl = urlParams.get('discount') || '';

  const [formData, setFormData] = useState({
    firstName: prefillFirst || '',
    lastName: prefillRest.join(' ') || '',
    dateOfBirth: '',
    email: emailFromUrl,
    password: '',
    confirmPassword: '',
    tier: planFromUrl || 'pro',
    billingCycle: 'monthly',
    discountCode: discountFromUrl,
    affiliateCode: refFromUrl || '',
    website: '',
  });

  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

  useEffect(() => {
    const existingUser = localStorage.getItem('user');
    if (existingUser) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  }, []);

  useEffect(() => {
    if (refFromUrl) apiService.trackAffiliateClick(refFromUrl).catch(() => {});
  }, [refFromUrl]);

  // Load Stripe.js, handling the case where the CDN script is blocked (ad
  // blocker / privacy extension). loadStripe() rejects with "Stripe.js not
  // available" / "Failed to load Stripe.js" when blocked — we catch it so it
  // never becomes an unhandled rejection, show a friendly fallback, and keep a
  // working retry. @stripe/stripe-js resets its internal cache on failure, so
  // calling loadStripe again re-injects a fresh script.
  const loadStripeKey = useCallback(async () => {
    setStripeStatus('loading');
    try {
      const res = await apiService.getStripePublishableKey();
      const key = res.data?.publishable_key;
      if (!key) throw new Error('Missing Stripe publishable key');
      const stripe = await loadStripe(key);
      if (!stripe) throw new Error('Stripe.js not available');
      setStripePromise(stripe);
      setStripeStatus('ready');
    } catch (err) {
      // Client-environment issue, not an app bug — log as a low-severity,
      // tagged warning so it doesn't trigger high-priority Sentry alerts.
      console.warn('Stripe.js failed to load (likely ad blocker / privacy extension):', err?.message || err);
      try {
        Sentry.captureMessage('Stripe.js blocked (client environment)', {
          level: 'warning',
          tags: { stripe_blocked: true, path: 'signup_payment' },
        });
      } catch (_) { /* Sentry optional */ }
      setStripePromise(null);
      setStripeStatus('error');
    }
  }, []);

  useEffect(() => {
    loadStripeKey();
  }, [loadStripeKey]);

  // Capture an abandoned-signup lead the first time the visitor reaches the
  // Payment step. Fire-and-forget — the backend emails a prefilled "finish
  // your signup" link (~1h + ~24h) if they don't complete. No-op for
  // free-access signups (no payment required).
  const leadSentRef = useRef(false);
  useEffect(() => {
    if (step !== 3 || leadSentRef.current) return;
    if (discountValidation?.grants_free_access || discountValidation?.discount_type === 'free_access') return;
    if (!formData.email || !formData.email.includes('@')) return;
    leadSentRef.current = true;
    apiService.recordSignupLead({
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      tier: formData.tier,
      billing_cycle: formData.billingCycle,
    }).catch(() => {});
  }, [step, formData.email, formData.firstName, formData.lastName, formData.tier, formData.billingCycle, discountValidation]);

  const handleValidateDiscount = async () => {
    if (!formData.discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }
    setIsValidatingDiscount(true);
    try {
      const res = await apiService.validateDiscountCode(formData.discountCode, formData.tier);
      setDiscountValidation({ valid: true, ...res.data });
      toast.success(`Discount applied: ${res.data.display}`);
    } catch (error) {
      setDiscountValidation({ valid: false, error: error.response?.data?.detail || 'Invalid code' });
      toast.error(error.response?.data?.detail || 'Invalid discount code');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validateStep1 = () => {
    if (formData.website) return true;
    if (!formData.firstName || !formData.lastName) { toast.error('Please enter your name'); return false; }
    if (!formData.email) { toast.error('Please enter your email'); return false; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return false; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
    if (!formData.dateOfBirth) { toast.error('Please enter your date of birth'); return false; }
    const birthDate = new Date(formData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 18) { toast.error('You must be 18 or older to sign up'); return false; }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2) setStep(3);
  };

  const handleSignupSuccess = (data) => {
    if (data?.requires_verification) {
      setVerificationEmail(formData.email);
      setShowVerificationMessage(true);
      toast.success('Please check your email to verify your account');
    } else {
      toast.success('Account created successfully!');
      navigate('/login');
    }
  };

  const handleFreeSignup = async () => {
    setIsLoading(true);
    try {
      const res = await apiService.signupFree({
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        password: formData.password,
        date_of_birth: formData.dateOfBirth,
        tier: formData.tier,
        discount_code: formData.discountCode,
      });
      localStorage.setItem('user', JSON.stringify(res.data));
      onLogin?.(res.data);
      toast.success('Free account created — welcome!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create free account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;
    setIsResending(true);
    try {
      await apiService.resendVerification(verificationEmail);
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to resend email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const selectedTier = TIERS.find(t => t.id === formData.tier);
  const basePrice = formData.billingCycle === 'annual' ? selectedTier?.price_annual : selectedTier?.price_monthly;

  const getDisplayPrice = () => {
    if (!discountValidation?.valid) return basePrice;
    if (discountValidation.discount_type === 'percentage') return Math.round(basePrice * (1 - discountValidation.discount_value / 100));
    if (discountValidation.discount_type === 'fixed') return Math.max(0, basePrice - discountValidation.discount_value);
    if (discountValidation.discount_type === 'free_months') return 0;
    return basePrice;
  };
  const price = getDisplayPrice();

  if (showVerificationMessage) {
    return (
      <AuthShell backTo={null} showLegal>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#E7F6EF' }}>
            <Mail className="w-7 h-7" style={{ color: '#059669' }} />
          </div>
          <h1 className="text-2xl">Check your email</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>
            We've sent a verification link to<br />
            <strong style={{ color: '#1D2939' }}>{verificationEmail}</strong>
          </p>
        </div>
        <div className="rounded-md border p-4 mb-6" style={{ borderColor: '#EAECF0', background: '#F8FAFC' }}>
          <h4 className="font-semibold mb-2" style={{ color: '#13294B' }}>Next steps:</h4>
          <ol className="text-sm list-decimal list-inside space-y-2" style={{ color: AUTH_SUBTLE }}>
            <li>Check your email inbox</li>
            <li>Click the verification link</li>
            <li>Start your 30-day free trial!</li>
          </ol>
        </div>
        <p className="text-center text-sm mb-3" style={{ color: AUTH_SUBTLE }}>Didn't receive the email?</p>
        <OutlineButton onClick={handleResendVerification} disabled={isResending}>{isResending ? 'Sending...' : 'Resend Verification Email'}</OutlineButton>
        <div className="pt-4 mt-4 border-t text-center" style={{ borderColor: '#EAECF0' }}>
          <Link to="/login" className="text-sm hover:opacity-70" style={{ color: AUTH_BLUE }}>Back to Login</Link>
        </div>
      </AuthShell>
    );
  }

  const ProgressHeader = (
    <div className="mb-6">
      <div className="flex items-center justify-center">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm" style={{ background: step >= s ? AUTH_BLUE : '#E4E9F0', color: step >= s ? '#fff' : '#98A2B3' }}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < 3 && <div className="w-14 h-0.5 mx-2 rounded" style={{ background: step > s ? AUTH_BLUE : '#E4E9F0' }} />}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-center gap-8 mt-3 text-sm">
        <span style={{ color: step >= 1 ? '#13294B' : '#98A2B3' }}>Account</span>
        <span style={{ color: step >= 2 ? '#13294B' : '#98A2B3' }}>Plan</span>
        <span style={{ color: step >= 3 ? '#13294B' : '#98A2B3' }}>Payment</span>
      </div>
    </div>
  );

  return (
    <AuthShell maxWidth="max-w-2xl" showLegal header={ProgressHeader}>
      <div className="text-center mb-6">
        <AuthLogo className="mx-auto mb-4" />
        <h1 className="text-2xl">
          {step === 1 && 'Create your account'}
          {step === 2 && 'Choose your plan'}
          {step === 3 && 'Add payment method'}
        </h1>
        <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>
          {step === 1 && 'Start your 30-day free trial'}
          {step === 2 && 'Select the plan that fits your needs'}
          {step === 3 && 'Your card will not be charged during the trial'}
        </p>
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <>
          <OutlineButton type="button" onClick={handleGoogleSignup}><GoogleIcon /> Continue with Google</OutlineButton>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: '#EAECF0' }} />
            <span className="text-sm" style={{ color: AUTH_SUBTLE }}>or sign up with email</span>
            <div className="flex-1 h-px" style={{ background: '#EAECF0' }} />
          </div>
          <div className="space-y-4">
            <div className="absolute left-[-9999px]" aria-hidden="true">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName" style={{ color: AUTH_LABEL }}>First Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                  <Input id="firstName" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="pl-10 h-11" placeholder="John" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" style={{ color: AUTH_LABEL }}>Last Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                  <Input id="lastName" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="pl-10 h-11" placeholder="Doe" required />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" style={{ color: AUTH_LABEL }}>Date of Birth</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                <Input id="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} className="pl-10 h-11" required />
              </div>
              <p className="text-xs" style={{ color: AUTH_SUBTLE }}>You must be 18 or older to sign up</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: AUTH_LABEL }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="pl-10 h-11" placeholder="you@example.com" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: AUTH_LABEL }}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="pl-10 h-11" placeholder="••••••••" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: AUTH_LABEL }}>Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
                <Input id="confirmPassword" type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="pl-10 h-11" placeholder="••••••••" required />
              </div>
            </div>
            <PrimaryButton onClick={handleNext}>Continue to Plan Selection</PrimaryButton>
          </div>
        </>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="rounded-md p-1 flex border" style={{ borderColor: '#E4E9F0', background: '#F2F4F7' }}>
              <button onClick={() => setFormData({ ...formData, billingCycle: 'monthly' })} className="px-4 py-2 rounded text-sm font-medium transition" style={{ background: formData.billingCycle === 'monthly' ? AUTH_BLUE : 'transparent', color: formData.billingCycle === 'monthly' ? '#fff' : AUTH_SUBTLE }}>Monthly</button>
              <button onClick={() => setFormData({ ...formData, billingCycle: 'annual' })} className="px-4 py-2 rounded text-sm font-medium transition" style={{ background: formData.billingCycle === 'annual' ? AUTH_BLUE : 'transparent', color: formData.billingCycle === 'annual' ? '#fff' : AUTH_SUBTLE }}>
                Annual <span style={{ color: formData.billingCycle === 'annual' ? '#BBF0D2' : '#059669' }} className="text-xs ml-1">Save 17%</span>
              </button>
            </div>
          </div>

          <div className="grid gap-4">
            {TIERS.map((tier) => {
              const isSelected = formData.tier === tier.id;
              return (
                <div
                  key={tier.id}
                  onClick={() => {
                    if (tier.contact_required) { window.open('mailto:support@drawready.com?subject=Enterprise%20Plan%20Inquiry', '_blank'); return; }
                    setFormData({ ...formData, tier: tier.id });
                    setDiscountValidation(null);
                  }}
                  className="p-4 rounded-md border cursor-pointer transition-all"
                  style={{ borderColor: isSelected ? AUTH_BLUE : '#E4E9F0', background: isSelected ? '#EEF2FB' : '#fff', borderWidth: isSelected ? 2 : 1 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg" style={{ color: '#13294B' }}>{tier.name}</h3>
                        {tier.popular && <span className="text-white text-xs px-2 py-0.5 rounded" style={{ background: AUTH_BLUE }}>Popular</span>}
                        {tier.contact_required && <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#EEF2FB', color: AUTH_BLUE }}>Contact Us</span>}
                      </div>
                      <p className="text-sm mt-1" style={{ color: AUTH_SUBTLE }}>{tier.features.slice(0, 2).join(' • ')}</p>
                    </div>
                    <div className="text-right">
                      {tier.contact_required ? (
                        <p className="text-lg font-bold" style={{ color: AUTH_BLUE }}>Custom Pricing</p>
                      ) : (
                        <>
                          <p className="text-2xl font-bold" style={{ color: '#13294B' }}>${formData.billingCycle === 'annual' ? tier.price_annual : tier.price_monthly}</p>
                          <p className="text-xs" style={{ color: AUTH_SUBTLE }}>/{formData.billingCycle === 'annual' ? 'year' : 'month'}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-md p-4 border" style={{ borderColor: '#EAECF0', background: '#F8FAFC' }}>
            <label className="text-sm mb-2 block" style={{ color: AUTH_LABEL }}>Have a discount code?</label>
            <div className="flex gap-2">
              <Input placeholder="Enter code" value={formData.discountCode} onChange={(e) => { setFormData({ ...formData, discountCode: e.target.value.toUpperCase() }); setDiscountValidation(null); }} className="flex-1" />
              <OutlineButton type="button" onClick={handleValidateDiscount} disabled={!formData.discountCode || isValidatingDiscount} className="!w-auto px-5 !h-11">
                {isValidatingDiscount ? 'Checking...' : 'Apply'}
              </OutlineButton>
            </div>
            {discountValidation && (
              <div className="mt-2 text-sm" style={{ color: discountValidation.valid ? '#059669' : '#DC2626' }}>
                {discountValidation.valid ? (
                  <span className="flex items-center gap-1"><Check className="w-4 h-4" /> {discountValidation.display} applied!</span>
                ) : (
                  <span className="flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {discountValidation.error}</span>
                )}
              </div>
            )}
          </div>

          {formData.affiliateCode && (
            <div className="rounded-md px-4 py-2 border text-sm" style={{ borderColor: '#D6E4FF', background: '#EEF2FB', color: AUTH_BLUE }}>
              Referred by partner: <span className="font-mono">{formData.affiliateCode}</span>
            </div>
          )}

          <div className="flex gap-3">
            <OutlineButton onClick={() => setStep(1)} className="flex-1">Back</OutlineButton>
            <PrimaryButton onClick={handleNext} className="flex-1">Continue to Payment</PrimaryButton>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="rounded-md p-4 border" style={{ borderColor: '#E4E9F0', background: '#F8FAFC' }}>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium" style={{ color: '#13294B' }}>{selectedTier?.name} Plan</p>
                <p className="text-sm" style={{ color: AUTH_SUBTLE }}>{formData.billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'}</p>
              </div>
              <div className="text-right">
                {discountValidation?.valid && basePrice !== price ? (
                  <>
                    <p className="text-sm line-through" style={{ color: '#98A2B3' }}>${basePrice}</p>
                    <p className="text-xl font-bold" style={{ color: '#059669' }}>${price}</p>
                  </>
                ) : (
                  <p className="text-xl font-bold" style={{ color: '#13294B' }}>${price}</p>
                )}
                <p className="text-xs" style={{ color: '#059669' }}>30-day free trial</p>
              </div>
            </div>
            {discountValidation?.valid && (
              <div className="mt-2 pt-2 border-t text-sm" style={{ borderColor: '#EAECF0', color: '#059669' }}>
                Discount: {discountValidation.display}
                {discountValidation.discount_type === 'free_months' && (
                  <span> ({discountValidation.free_months} month{discountValidation.free_months > 1 ? 's' : ''} free after trial)</span>
                )}
              </div>
            )}
          </div>

          {(discountValidation?.valid && (discountValidation?.grants_free_access || discountValidation?.discount_type === 'free_access')) ? (
            <div className="space-y-4" data-testid="free-access-panel">
              <div className="rounded-md p-4 border text-center" style={{ borderColor: '#BBF0D2', background: '#E7F6EF' }}>
                <p className="font-semibold" style={{ color: '#065F46' }}>No card required</p>
                <p className="text-sm mt-1" style={{ color: '#047857' }}>Your free-access code covers this account. Just create your account below.</p>
              </div>
              <PrimaryButton onClick={handleFreeSignup} disabled={isLoading} data-testid="create-free-account-btn" style={{ background: '#059669' }}>
                {isLoading ? 'Creating account...' : 'Create free account'}
              </PrimaryButton>
            </div>
          ) : stripeStatus === 'ready' && stripePromise ? (
            <Elements stripe={stripePromise}>
              <CardForm formData={formData} discountCode={discountValidation?.valid ? formData.discountCode : null} affiliateCode={formData.affiliateCode} onSuccess={handleSignupSuccess} setIsLoading={setIsLoading} isLoading={isLoading} />
            </Elements>
          ) : stripeStatus === 'error' ? (
            <div className="space-y-4" data-testid="stripe-blocked-fallback">
              <div className="rounded-md p-5 border text-center" style={{ borderColor: '#FEE4B3', background: '#FFF8EC' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#FEF0D4' }}>
                  <ShieldAlert className="w-6 h-6" style={{ color: '#B45309' }} />
                </div>
                <h3 className="font-semibold text-lg" style={{ color: '#92400E' }}>We couldn't load our secure payment form</h3>
                <p className="text-sm mt-2" style={{ color: '#9A5B13' }}>
                  This is usually caused by an ad blocker or privacy extension blocking our payment provider (Stripe). Try disabling it for this page, or switch to a different browser — your information is safe and secure.
                </p>
              </div>
              <PrimaryButton onClick={loadStripeKey} data-testid="stripe-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Try again
              </PrimaryButton>
            </div>
          ) : (
            <div className="text-center py-8" data-testid="stripe-loading">
              <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-2" style={{ borderColor: AUTH_BLUE, borderTopColor: 'transparent' }} />
              <p style={{ color: AUTH_SUBTLE }}>Loading payment form...</p>
            </div>
          )}

          <OutlineButton onClick={() => setStep(2)}>Back to Plan Selection</OutlineButton>
        </div>
      )}

      <p className="text-xs text-center pt-4" style={{ color: AUTH_SUBTLE }}>
        By signing up, you agree to our{' '}
        <Link to="/terms" className="hover:underline" style={{ color: AUTH_BLUE }}>Terms of Service</Link>{' '}and{' '}
        <Link to="/privacy" className="hover:underline" style={{ color: AUTH_BLUE }}>Privacy Policy</Link>
      </p>

      <p className="text-center text-sm mt-4" style={{ color: AUTH_SUBTLE }}>
        Already have an account?{' '}
        <Link to="/login" className="font-semibold hover:opacity-70" style={{ color: AUTH_BLUE }}>Sign in</Link>
      </p>
    </AuthShell>
  );
}

export default Signup;
